import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * Keep three viewport heights on either side of a product grid ready to render.
 * Distant cards retain the theme's content-visibility:auto and native image loading.
 * Observe row boxes, not skipped descendants; no per-scroll scans or measurements.
 */
class ProductGridRenderBuffer {
  #owner;
  #grid;
  #cards = new Map();
  #rows = new WeakMap();
  #intersection;
  #sizes;
  #mutations;
  #frame;
  #running = false;
  #width = 0;

  constructor(owner) {
    this.#owner = owner;
  }

  connect() {
    if (this.#running || !window.IntersectionObserver || !window.ResizeObserver) return;
    this.#running = true;
    this.#sizes = new ResizeObserver((entries) => {
      if (!this.#running) return;
      for (const { target, contentRect } of entries) {
        const record = this.#cards.get(this.#rows.get(target));
        if (!record?.ready || contentRect.height <= 0) continue;
        // Preserve the measured content height when a card leaves the ready area.
        const value = `auto ${contentRect.height}px`;
        if (target.style.getPropertyValue('contain-intrinsic-block-size') !== value) {
          target.style.setProperty('contain-intrinsic-block-size', value);
        }
      }
    });
    this.#mutations = new MutationObserver(() => this.refresh());
    window.addEventListener('resize', this.#onResize, { passive: true });
    this.#width = window.innerWidth;
    this.refresh();
  }

  refresh() {
    if (!this.#running) return;
    const grid = this.#owner.querySelector('.product-grid[ref="grid"]');
    if (grid !== this.#grid) {
      this.#mutations.disconnect();
      this.#intersection?.disconnect();
      for (const row of this.#cards.keys()) this.#forget(row);
      this.#grid = grid;
      this.#intersection = undefined;
      if (!grid) return;
      this.#mutations.observe(grid, { childList: true });
      this.#observeArea();
    }
    if (!grid) return;
    for (const [row, record] of this.#cards) {
      if (row.parentElement !== grid || row.querySelector('.product-grid__card') !== record.card) {
        this.#forget(row);
      } else if (record.ready) {
        // Section rendering can morph attributes on an existing card.
        record.card.style.setProperty('content-visibility', 'visible');
      }
    }
    for (const row of grid.children) {
      if (this.#cards.has(row)) continue;
      const card = row.querySelector('.product-grid__card');
      if (!card) continue;
      this.#cards.set(row, {
        card,
        ready: false,
        visibility: this.#savedStyle(card, 'content-visibility'),
        intrinsic: this.#savedStyle(card, 'contain-intrinsic-block-size'),
      });
      this.#rows.set(card, row);
      this.#intersection.observe(row);
    }
  }

  invalidate() {
    if (!this.#running) return;
    // Width and grid-layout changes invalidate measured fallback heights.
    for (const record of this.#cards.values()) {
      this.#restoreStyle(record.card, 'contain-intrinsic-block-size', record.intrinsic);
      if (record.ready) {
        this.#sizes.unobserve(record.card);
        this.#sizes.observe(record.card);
      }
    }
    this.#observeArea();
  }

  disconnect() {
    this.#running = false;
    window.removeEventListener('resize', this.#onResize);
    if (this.#frame != null) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
    this.#mutations?.disconnect();
    this.#intersection?.disconnect();
    for (const row of this.#cards.keys()) this.#forget(row);
    this.#sizes?.disconnect();
    this.#grid = undefined;
    this.#intersection = undefined;
  }

  #observeArea() {
    this.#intersection?.disconnect();
    if (!this.#grid) return;
    const margin = Math.ceil((window.innerHeight || document.documentElement.clientHeight) * 3);
    const observer = new IntersectionObserver((entries) => {
      if (!this.#running || this.#intersection !== observer) return;
      for (const entry of entries) {
        const record = this.#cards.get(entry.target);
        if (!record || record.ready === entry.isIntersecting) continue;
        record.ready = entry.isIntersecting;
        if (record.ready) {
          record.card.style.setProperty('content-visibility', 'visible');
          this.#sizes.observe(record.card);
        } else {
          this.#sizes.unobserve(record.card);
          this.#restoreStyle(record.card, 'content-visibility', record.visibility);
        }
      }
    }, { rootMargin: `${margin}px 0px`, threshold: 0 });
    this.#intersection = observer;
    for (const row of this.#cards.keys()) observer.observe(row);
  }

  #onResize = () => {
    if (this.#frame != null) cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      if (!this.#running) return;
      if (this.#width !== window.innerWidth) {
        this.#width = window.innerWidth;
        this.invalidate();
      } else {
        this.#observeArea();
      }
    });
  };

  #forget(row) {
    const record = this.#cards.get(row);
    if (!record) return;
    this.#intersection?.unobserve(row);
    this.#sizes?.unobserve(record.card);
    this.#restoreStyle(record.card, 'content-visibility', record.visibility);
    this.#restoreStyle(record.card, 'contain-intrinsic-block-size', record.intrinsic);
    this.#rows.delete(record.card);
    this.#cards.delete(row);
  }

  #savedStyle(card, name) {
    return [card.style.getPropertyValue(name), card.style.getPropertyPriority(name)];
  }

  #restoreStyle(card, name, [value, priority]) {
    if (value) card.style.setProperty(name, value, priority);
    else card.style.removeProperty(name);
  }
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #renderBuffer = new ProductGridRenderBuffer(this);

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#syncImageSizes();
    this.#renderBuffer.connect();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncImageSizes();
    this.#renderBuffer.refresh();
  }

  disconnectedCallback() {
    this.#renderBuffer.disconnect();
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#syncImageSizes();
    this.#renderBuffer.invalidate();
  }

  #syncImageSizes() {
    const { grid } = this.refs;
    if (!grid) return;
    const singleColumn = grid.getAttribute('product-grid-view') === 'mobile-single';

    for (const image of grid.querySelectorAll('.card-gallery img[sizes]')) {
      const original = image.dataset.gridImageSizes ?? image.sizes;
      if (!original) continue;
      image.dataset.gridImageSizes = original;
      // The single-column mobile view needs full-width images, not the two-column hint.
      const sizes = singleColumn ? `(max-width: 749px) 100vw, ${original}` : original;
      if (image.sizes !== sizes) image.sizes = sizes;
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
